/**
 * useDMMessages Hook
 *
 * React hook for managing direct message conversations between friends.
 * Integrates with existing dm_conversations and dm_messages tables.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

// Types
export interface DMConversation {
  id: string;
  participant_1: string;
  participant_2: string;
  last_message: string | null;
  last_message_at: string | null;
  created_at: string;
  // Joined data
  other_user?: {
    id: string;
    username: string;
    avatar_url: string | null;
    status?: string;
  };
  unread_count?: number;
}

export interface DMMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  is_deleted: boolean;
  created_at: string;
  // Joined profile data
  sender?: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
}

interface UseDMMessagesReturn {
  // State
  conversations: DMConversation[];
  activeConversation: DMConversation | null;
  messages: DMMessage[];
  loading: boolean;
  error: string | null;

  // Conversation operations
  loadConversations: () => Promise<void>;
  getOrCreateConversation: (otherUserId: string) => Promise<string | null>;
  setActiveConversation: (conversation: DMConversation | null) => void;

  // Message operations
  loadMessages: (conversationId: string, limit?: number) => Promise<void>;
  sendMessage: (content: string) => Promise<boolean>;
  markAsRead: (conversationId: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<boolean>;

  // Block operations
  blockUser: (userId: string) => Promise<boolean>;
  unblockUser: (userId: string) => Promise<boolean>;
  isBlocked: (userId: string) => Promise<boolean>;

  // Real-time
  subscribeToMessages: () => void;
  unsubscribeFromMessages: () => void;
}

export function useDMMessages(userId: string | null): UseDMMessagesReturn {
  const [conversations, setConversations] = useState<DMConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<DMConversation | null>(null);
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  const conversationSubscriptionRef = useRef<RealtimeChannel | null>(null);

  // Load all conversations for the user
  const loadConversations = useCallback(async () => {
    if (!userId) {
      setConversations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get conversations where user is a participant
      const { data: convos, error: convosError } = await supabase
        .from('dm_conversations')
        .select('*')
        .or(`participant_1.eq.${userId},participant_2.eq.${userId}`)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (convosError) {
        console.error('Error loading conversations:', convosError);
        setError('Failed to load conversations');
        return;
      }

      // For each conversation, get the other user's profile
      const enrichedConvos = await Promise.all(
        (convos || []).map(async (conv) => {
          const otherUserId = conv.participant_1 === userId ? conv.participant_2 : conv.participant_1;

          // Get profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, username, avatar_url, status')
            .eq('id', otherUserId)
            .single();

          // Get unread count
          const { count } = await supabase
            .from('dm_messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('is_read', false)
            .neq('sender_id', userId);

          return {
            ...conv,
            other_user: profile || { id: otherUserId, username: 'Unknown', avatar_url: null },
            unread_count: count || 0,
          };
        })
      );

      setConversations(enrichedConvos);
    } catch (err) {
      console.error('Error in loadConversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial load
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Get or create a conversation with another user
  const getOrCreateConversation = useCallback(async (otherUserId: string): Promise<string | null> => {
    if (!userId || userId === otherUserId) return null;

    try {
      // Check for existing conversation (order-independent)
      const { data: existing, error: findError } = await supabase
        .from('dm_conversations')
        .select('*')
        .or(
          `and(participant_1.eq.${userId},participant_2.eq.${otherUserId}),and(participant_1.eq.${otherUserId},participant_2.eq.${userId})`
        )
        .maybeSingle();

      if (findError && findError.code !== 'PGRST116') {
        console.error('Error finding conversation:', findError);
        return null;
      }

      if (existing) {
        return existing.id;
      }

      // Create new conversation
      const { data: newConvo, error: createError } = await supabase
        .from('dm_conversations')
        .insert({
          participant_1: userId,
          participant_2: otherUserId,
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating conversation:', createError);
        return null;
      }

      // Refresh conversations list
      await loadConversations();
      return newConvo.id;
    } catch (err) {
      console.error('Error in getOrCreateConversation:', err);
      return null;
    }
  }, [userId, loadConversations]);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId: string, limit: number = 50) => {
    try {
      const { data, error: loadError } = await supabase
        .from('dm_messages')
        .select(`
          *,
          sender:sender_id (
            id,
            username,
            avatar_url
          )
        `)
        .eq('conversation_id', conversationId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (loadError) {
        console.error('Error loading messages:', loadError);
        return;
      }

      // Reverse to show in chronological order
      setMessages((data || []).reverse() as DMMessage[]);
    } catch (err) {
      console.error('Error in loadMessages:', err);
    }
  }, []);

  // Load messages when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.id);
      markAsRead(activeConversation.id);
    } else {
      setMessages([]);
    }
  }, [activeConversation, loadMessages]);

  // Send a message
  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    if (!userId || !activeConversation || !content.trim()) return false;

    try {
      const trimmedContent = content.trim();

      // Insert message
      const { data: newMessage, error: insertError } = await supabase
        .from('dm_messages')
        .insert({
          conversation_id: activeConversation.id,
          sender_id: userId,
          content: trimmedContent,
        })
        .select(`
          *,
          sender:sender_id (
            id,
            username,
            avatar_url
          )
        `)
        .single();

      if (insertError) {
        console.error('Error sending message:', insertError);
        setError(insertError.message);
        return false;
      }

      // Update conversation with last message
      await supabase
        .from('dm_conversations')
        .update({
          last_message: trimmedContent,
          last_message_at: new Date().toISOString(),
        })
        .eq('id', activeConversation.id);

      // Add to local messages (optimistic update)
      setMessages(prev => [...prev, newMessage as DMMessage]);

      // Refresh conversations to update order
      loadConversations();

      return true;
    } catch (err) {
      console.error('Error in sendMessage:', err);
      setError('Failed to send message');
      return false;
    }
  }, [userId, activeConversation, loadConversations]);

  // Mark messages as read
  const markAsRead = useCallback(async (conversationId: string) => {
    if (!userId) return;

    try {
      await supabase
        .from('dm_messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId)
        .eq('is_read', false);

      // Update local unread count
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
        )
      );
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  }, [userId]);

  // Delete a message (soft delete)
  const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('dm_messages')
        .update({ is_deleted: true })
        .eq('id', messageId)
        .eq('sender_id', userId);

      if (deleteError) {
        console.error('Error deleting message:', deleteError);
        return false;
      }

      // Remove from local messages
      setMessages(prev => prev.filter(m => m.id !== messageId));
      return true;
    } catch (err) {
      console.error('Error in deleteMessage:', err);
      return false;
    }
  }, [userId]);

  // Block a user
  const blockUser = useCallback(async (blockedUserId: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { error: blockError } = await supabase
        .from('blocked_users')
        .insert({
          user_id: userId,
          blocked_user_id: blockedUserId,
        });

      if (blockError) {
        console.error('Error blocking user:', blockError);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error in blockUser:', err);
      return false;
    }
  }, [userId]);

  // Unblock a user
  const unblockUser = useCallback(async (blockedUserId: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { error: unblockError } = await supabase
        .from('blocked_users')
        .delete()
        .eq('user_id', userId)
        .eq('blocked_user_id', blockedUserId);

      if (unblockError) {
        console.error('Error unblocking user:', unblockError);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error in unblockUser:', err);
      return false;
    }
  }, [userId]);

  // Check if a user is blocked
  const isBlocked = useCallback(async (otherUserId: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data, error } = await supabase
        .from('blocked_users')
        .select('id')
        .eq('user_id', userId)
        .eq('blocked_user_id', otherUserId)
        .maybeSingle();

      if (error) return false;
      return !!data;
    } catch {
      return false;
    }
  }, [userId]);

  // Subscribe to real-time messages
  const subscribeToMessages = useCallback(() => {
    if (!userId || !activeConversation) return;

    // Unsubscribe from previous
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    // Subscribe to new messages in this conversation
    subscriptionRef.current = supabase
      .channel(`dm_messages:${activeConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'dm_messages',
          filter: `conversation_id=eq.${activeConversation.id}`,
        },
        async (payload) => {
          // Only add if not from us (avoid duplicates from optimistic update)
          if (payload.new.sender_id !== userId) {
            // Fetch with profile
            const { data } = await supabase
              .from('dm_messages')
              .select(`
                *,
                sender:sender_id (
                  id,
                  username,
                  avatar_url
                )
              `)
              .eq('id', payload.new.id)
              .single();

            if (data) {
              setMessages(prev => [...prev, data as DMMessage]);
              // Mark as read since we're viewing
              markAsRead(activeConversation.id);
            }
          }
        }
      )
      .subscribe();
  }, [userId, activeConversation, markAsRead]);

  // Unsubscribe from messages
  const unsubscribeFromMessages = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
  }, []);

  // Subscribe when conversation changes
  useEffect(() => {
    if (activeConversation) {
      subscribeToMessages();
    }
    return () => {
      unsubscribeFromMessages();
    };
  }, [activeConversation, subscribeToMessages, unsubscribeFromMessages]);

  // Subscribe to conversation updates (for unread counts)
  useEffect(() => {
    if (!userId) return;

    conversationSubscriptionRef.current = supabase
      .channel(`dm_conversations:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dm_conversations',
        },
        () => {
          // Reload conversations when any change happens
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      if (conversationSubscriptionRef.current) {
        conversationSubscriptionRef.current.unsubscribe();
      }
    };
  }, [userId, loadConversations]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeFromMessages();
      if (conversationSubscriptionRef.current) {
        conversationSubscriptionRef.current.unsubscribe();
      }
    };
  }, [unsubscribeFromMessages]);

  return {
    conversations,
    activeConversation,
    messages,
    loading,
    error,
    loadConversations,
    getOrCreateConversation,
    setActiveConversation,
    loadMessages,
    sendMessage,
    markAsRead,
    deleteMessage,
    blockUser,
    unblockUser,
    isBlocked,
    subscribeToMessages,
    unsubscribeFromMessages,
  };
}
